import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HowToUploadLink from '@/components/common/HowToUploadLink';
import { cn } from '@/lib/utils';
import { WIZARD_FOCUS } from './components/fileCardStyles';
import fileFinityLogo from '@/assets/filefinity_navbar_logo.png';
import onefinityLogo from '@/assets/onefinity_logo.png';
import { selectV2, resetAll } from './v2Upload.slice';
import useDraftResume from './useDraftResume';
import useFocusField from './useFocusField';
import useTour from './useTour';
import LivePreview from './components/LivePreview';
import SubmittedScreen from './components/SubmittedScreen';
import ProjectProgress from './components/ProjectProgress';
import { V2MultiStage } from './v2Upload.d';
import ChooseTypeStep from './steps/ChooseTypeStep';
import BasicsStep from './steps/BasicsStep';
import WorkpieceCompleteStep from './steps/WorkpieceCompleteStep';
import GroupReviewStep from './steps/GroupReviewStep';
import FilesStep from './steps/FilesStep';
import ToolsStep from './steps/ToolsStep';
import JobDetailsStep from './steps/JobDetailsStep';
import LicenseStep from './steps/LicenseStep';
import SignoffStep from './steps/SignoffStep';

const STEP_COMPONENTS = [
  ChooseTypeStep,
  BasicsStep,
  FilesStep,
  ToolsStep,
  JobDetailsStep,
  LicenseStep,
  SignoffStep,
];

/** Multi runs the same six steps per workpiece, then two screens of its own. */
function pickStep(step: number, multiStage: V2MultiStage, multi: boolean) {
  if (!multi) return STEP_COMPONENTS[step];
  if (multiStage === 'interstitial') return WorkpieceCompleteStep;
  if (multiStage === 'review') return GroupReviewStep;
  return STEP_COMPONENTS[step];
}

/** Kept on the done screen too, so submitting doesn't drop the user out of the
 * editor chrome into a bare page. */
export function TitleBar({
  onClose,
  onHome,
  onTour,
}: {
  onClose: () => void;
  onHome: () => void;
  onTour?: () => void;
}) {
  return (
    <div className="relative z-20 flex-none flex items-center gap-3 px-5 py-2 bg-green-950 shadow-[0_1px_3px_hsl(var(--shadow-color)/0.18)]">
      <button
        type="button"
        onClick={onHome}
        title="Back to Filefinity"
        className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        <img
          src={fileFinityLogo}
          alt="Filefinity"
          className="h-9 w-auto object-contain"
        />
      </button>
      <span className="h-7 w-px bg-white/20" />
      <a
        href="https://www.onefinitycnc.com/"
        target="_blank"
        rel="noopener noreferrer"
        title="Onefinity"
        className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        <img
          src={onefinityLogo}
          alt="Onefinity"
          className="h-9 w-auto object-contain"
        />
      </a>
      <span className="grow" />
      <HowToUploadLink />
      {onTour && (
        <Button
          variant="ghost"
          size="sm"
          title="Show me around this step"
          className="gap-1.5 text-neutral-100 hover:bg-white/10 hover:text-green-300"
          onClick={onTour}
        >
          <HelpCircle className="w-4 h-4" />
          Show me around
        </Button>
      )}
      <span data-focus="exit">
        <Button
          variant="outline"
          size="sm"
          title="Close the editor"
          className="gap-1.5 border-white/25 bg-transparent text-neutral-100 hover:bg-white/10 hover:text-white"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
          Exit
        </Button>
      </span>
    </div>
  );
}

// fallow-ignore-next-line complexity
export default function UploadWizard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    step,
    basics,
    submitted,
    postId,
    mode,
    groupId,
    multiStage,
    groupDetails,
    groupSubmitCounts,
  } = useSelector(selectV2);
  const multi = mode === 'multi';
  const CurrentStep = pickStep(step, multiStage, multi);
  useDraftResume();
  useFocusField();
  const startTour = useTour();

  // Leave the finished draft's URL behind as well as its state, or a refresh
  // would resume the project just completed instead of starting the new one.
  const startAnother = () => {
    dispatch(resetAll());
    navigate('/project/v2/upload', { replace: true });
  };

  // Same reset: the wizard's state is done with, and leaving it behind would
  // have the post's URL resume a draft that is already submitted.
  const viewPost = () => {
    dispatch(resetAll());
    navigate(`/model/${postId}`);
  };

  // Mid-wizard, the draft is already on the server and its URL resumes it, so
  // leaving keeps the state. Once submitted there is nothing left to come back
  // to — clear it, or reopening the wizard would land on the done screen.
  const clearIfSubmitted = () => {
    if (submitted) dispatch(resetAll());
  };

  const goHome = () => {
    clearIfSubmitted();
    navigate('/');
  };

  const closeEditor = () => {
    clearIfSubmitted();
    // idx, not location.key: creating a draft replaces the entry, which mints a
    // fresh key while leaving the wizard still first in history — going back
    // from there leaves the site.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/');
  };

  const viewGroup = () => {
    dispatch(resetAll());
    navigate(`/project-groups/${groupId}`);
  };

  const onViewSubmitted = () => {
    if (multi) return viewGroup();
    return postId ? viewPost() : undefined;
  };

  if (submitted) {
    return (
      <div className="bg-card h-full flex flex-col font-secondary">
        <TitleBar onClose={closeEditor} onHome={goHome} />
        <div className="flex-1 overflow-y-auto bg-background">
          <SubmittedScreen
            title={multi ? groupDetails.title : basics.title}
            onUploadAnother={startAnother}
            onViewPost={multi || postId ? onViewSubmitted : undefined}
            group={multi ? (groupSubmitCounts ?? undefined) : undefined}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-secondary h-full flex flex-col font-secondary">
      <TitleBar onClose={closeEditor} onHome={goHome} onTour={startTour} />

      {/* Body: main + rail */}
      <div className="flex flex-1 min-h-0">
        {/* Main area. Keyed on the step so each one settles in as it arrives —
            the rail's indicator already animated the change; the content used to
            hard-cut, so only half the UI acknowledged the navigation. */}
        <div
          className={cn(
            'flex-1 flex flex-col ml-1.5 sm:ml-3 mt-3 rounded-t-2xl border border-b-0 shadow-md p-4 sm:p-7 pb-0 sm:pb-0 min-w-0 overflow-y-auto custom-scrollbar bg-card',
            WIZARD_FOCUS
          )}
        >
          {CurrentStep && (
            <div
              key={multi ? `${multiStage}-${step}` : step}
              // flex-1 (in the flex-col parent) lets a short step — the upload
              // loader — grow to fill the whole area, and gives each step's
              // `min-h-full` root the height its sticky footer sits against.
              className="flex-1 animate-in fade-in slide-in-from-bottom-2 duration-base ease-out-strong fill-mode-both motion-reduce:animate-none"
            >
              <CurrentStep />
            </div>
          )}
        </div>

        {/* Right rail */}
        <aside
          aria-label="Project summary"
          className="relative z-10 w-[320px] flex-none p-5 hidden md:block overflow-y-auto custom-scrollbar"
        >
          <div data-focus="preview">
            <LivePreview />
          </div>
          <div className="mt-5" data-focus="progress">
            <ProjectProgress />
          </div>
        </aside>
      </div>
    </div>
  );
}
